<template>
    <div class="dashboard">
        <div class="container-fluid">
        <Header :profile="profile"></Header>
            <div class="row">
                <Sidebar :profile="profile" />
                <MainContent :profile="profile"/>
            </div>
        </div>
        <LoadingOverlay :show="showInitialLoadingScreen" :label="loadingLabel" />
        <UpgradeModal ref="upgradeModal" :entry-count="entriesUsage || 0" :scheduled-deletion-date="scheduledDeletionDate" />
        <LockdownPopup ref="lockdownPopup" />
    </div>
</template>

<script>
    import MainContent from "../components/MainContent";
    import Sidebar from "../components/Sidebar";
    import Header from "../components/Header";
    import LoadingOverlay from "../components/LoadingOverlay";
    import UpgradeModal from "../components/UpgradeModal.vue";
    import EventBus from '../event-bus';
    import store from "@/store";
    import { TRIAL_CONFIG } from '@/constants/trial';
import LockdownPopup from "../components/LockdownPopup.vue";

    export default {
        name: "Dashboard",
        components: {LoadingOverlay, MainContent, Sidebar, Header, UpgradeModal, LockdownPopup },
        data: function() {
            return {
                showInitialLoadingScreen: true,
                sidebarMethod: '',
                profile: {
                    firstName: '',
                    lastName: ''
                },
            date: new Date(),
            entriesUsage: 0,
            scheduledDeletionDate: null
            }
        },
        computed:  {
          loadingLabel: {
              get() {
                  let greeting = 'Hello';
                  if (this.date.getHours() < 12) {
                      greeting = 'Good morning';
                  } else if (this.date.getHours() >= 12 && this.date.getHours() < 16) {
                      greeting = 'Good afternoon';
                  } else {
                      greeting = 'Good evening';
                  }
                  if (this.profile.firstName !== '') {
                      return greeting + ' ' + this.profile.firstName;
                  } else {
                      return greeting;
                  }
              }
          }
        },
        methods: {
            updateProfileInformation: function()
            {
                return this.$api.v2.get('profile')
                .then(response =>
                {
                    this.profile = response.data;
                    this.storeMembershipStatus();
                })
                .catch((error) => {
                  console.error('An error occurred while fetching the profile:', error);
                  // Optionally set fallback values
                  // this.name = 'Unknown';
                });
            },
            storeMembershipStatus: function()
            {
                this.$store.dispatch('authentication/setFullMembershipStatus', {fullMember: this.profile.fullMember});
                this.$gtag.config({
                    'membership': this.profile.fullMember ? 'premium' : 'basic'
                });
            },
            loadTrialStatus: function()
            {
                // Get account creation date from membership data instead of profile
                this.$api.v2.get('profile/membership').then((resp) =>
                {
                    this.processTrialStatus(resp.body);
                }).catch((error) =>
                {
                    console.error('Failed to load membership data:', error);
                });
            },
            processTrialStatus: function(membershipData)
            {
                const accountCreatedDate = membershipData.memberSince;

                // Don't show trial popup if user is a full member
                if (this.profile.fullMember)
                {
                    return;
                }

                const accountCreated = new Date(accountCreatedDate);
                const cutoffDate = new Date(TRIAL_CONFIG.LEGACY_CUTOFF_DATE);

                // Base trial status object
                const trialStatus =
                {
                    accountCreatedDate: accountCreated.toISOString(),
                };

                // Check if user has ever had a membership (endDate exists = past membership)
                const hasHadMembership = membershipData.endDate !== null && membershipData.endDate !== undefined;

                if (accountCreated >= cutoffDate && !hasHadMembership)
                {
                    // Trial user only if: created after Aug 1st, 2025 AND never had a membership
                    const trialEndDate = new Date(accountCreated);
                    trialEndDate.setMonth(trialEndDate.getMonth() + TRIAL_CONFIG.TRIAL_DURATION_MONTHS);

                    // Get entry count from store (updated by SidebarContent)
                    const entryCount = this.$store.getters['authentication/getTrialStatus'].trialEntriesUsed || 0;
                    this.entriesUsage = entryCount;

                    // Set trial user specific properties
                    trialStatus.isTrialUser = true;
                    trialStatus.trialEndDate = trialEndDate.toISOString();
                    trialStatus.trialEntriesUsed = entryCount;
                    trialStatus.trialEntriesLimit = TRIAL_CONFIG.ENTRY_LIMIT;
                    trialStatus.isTrialExpired = new Date() > trialEndDate;

                    // Check if trial expired and set deletion date
                    if (trialStatus.isTrialExpired)
                    {
                        const deletionDate = new Date(trialEndDate);
                        deletionDate.setMonth(deletionDate.getMonth() + TRIAL_CONFIG.GRACE_PERIOD_MONTHS);
                        trialStatus.deletionDate = deletionDate.toISOString();
                    }
                }
                else
                {
                    // Not a trial user: legacy account OR has had membership before
                    // FULLY LOCKED DOWN - go straight to deletion countdown
                    const deletionDate = new Date(TRIAL_CONFIG.LEGACY_DELETION_DATE);

                    // Set locked down user specific properties
                    trialStatus.isTrialUser = false;
                    trialStatus.isTrialExpired = true;
                    trialStatus.deletionDate = deletionDate.toISOString();
                }

                this.$store.dispatch('authentication/setTrialStatus', trialStatus);

                // Load scheduled deletion date from database before showing modal
                this.$api.v2.get('user-account/me/scheduled-deletion')
                    .then((resp) => {
                        if (resp.body.scheduled) {
                            this.$store.dispatch('authentication/setScheduledDeletionDate', resp.body.scheduledDate);
                        }
                    })
                    .catch((err) => {
                        console.error('Failed to load scheduled deletion date', err);
                    })

            },
            showTrialModal: function()
            {
                // Show the upgrade modal
                if (this.$refs.upgradeModal)
                {
                    this.$refs.upgradeModal.show();
                }
            },
            showLockdownPopup: function()
            {
                if (this.$refs.lockdownPopup)
                {
                    this.$refs.lockdownPopup.show();
                }
            },
            loadSettings()
            {
                this.$api.v2.get('settings/dashboard').then((resp) => {
                    this.$store.dispatch('settings/storeSettings', resp.body);
                }).catch((resp) => {
                    this.$httpError('Failed to load settings', resp);
                })
            },
            loadGoverningBodies()
            {
              this.$api.v2.get('profile/governing-bodies').then((resp) =>
              {
                this.$store.dispatch('governingBodies/storeGoverningBodies', resp.body);
              }).catch((err) =>
              {
                this.$httpError('Failed to get governing bodies', err);
              });
            },
            loadFeatures()
            {
                this.$api.v2.get('features/user').then((resp) =>
                {
                    let featuresObj = resp.body.features;
                    let features = [];
                    for (let featureKey in featuresObj)
                    {
                        if (featuresObj[featureKey])
                        {
                            features.push(featureKey);
                        }
                    }
                    this.$store.dispatch('features/storeFeatures', features);
                }).catch((resp) =>
                {
                    this.$httpError('Failed to load features', resp);
                })
            },
            showToast({title, message}) {
                this.$toast('success', title, message, 5000);
            }
        },
        created()
        {
            if (process.env.VUE_APP_SHOW_LOADER)
            {
                setTimeout(() =>
                {
                    this.showInitialLoadingScreen = false;
                }, 2000);
            }
            else
            {
                this.showInitialLoadingScreen = false;
            }

            this.updateProfileInformation().then(() => {
                this.loadTrialStatus();
            });
            this.loadSettings();
            this.loadFeatures();
            this.loadGoverningBodies();

            EventBus.$on('user-changed', this.updateProfileInformation);
            EventBus.$on('settings-updated', this.loadSettings);
            EventBus.$on('show-toast', this.showToast);
            EventBus.$on('show-upgrade-modal', this.showTrialModal);
            EventBus.$on('show-lockdown-popup', this.showLockdownPopup);
        },
        destroyed()
        {
            EventBus.$off('show-toast', this.showToast);
            EventBus.$off('show-upgrade-modal', this.showTrialModal);
            EventBus.$off('show-lockdown-popup', this.showLockdownPopup);
        }
    }
</script>

<style scoped>
    .dashboard {
        min-height: 100%;
    }
</style>
